/**
 * PracticeTestContainer component to handle state management and business logic
 */
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import PracticeTest from '../components/PracticeTest'; // Import the wrapper component
import { 
  useQuestionState, 
  useSessionSetup, 
  useQuestionsData, 
  useTimeTracker 
} from '../hooks';
import services from '../services';
import examSessionStorage from '../services/examSessionStorage';
import { useTheme } from '../contexts/ThemeContext';

/**
 * Container component that manages state and business logic for PracticeTest
 * @returns {JSX.Element} PracticeTest view component with props
 */
const PracticeTestContainer = () => {  // URL parameters for resume functionality - support both path params and query params
  const { id: pathExamId } = useParams();
  const [searchParams] = useSearchParams();
  const queryExamId = searchParams.get('examId');
  const resumeParam = searchParams.get('resume');
  
  // Get preselection parameters from URL
  const categoryParam = searchParams.get('category');
  const examTypeParam = searchParams.get('examType');
  
  // Prioritize path parameter over query parameter for exam ID
  const examIdParam = pathExamId || queryExamId;
    // Context and services
  const { isDarkMode } = useTheme();
  const themeStyles = useMemo(() => services.theme.getThemeStyles(isDarkMode), [isDarkMode]);
    // Category data for session setup
  const [systemOptions, setSystemOptions] = useState([]);
  const [disciplineOptions, setDisciplineOptions] = useState([]);
  const [examTypes, setExamTypes] = useState([]);
  const [userFolders, setUserFolders] = useState([]);
  const [isLoadingFolders, setIsLoadingFolders] = useState(true);
  const [isLoadingExamTypes, setIsLoadingExamTypes] = useState(true);
  // Create initial setup with preselected values if available
  const initialSetup = useMemo(() => {
    // Don't set examType here - we'll handle it in useEffect after loading exam types
    return {};
  }, []);
  // Custom hooks for state management
  const questionState = useQuestionState();
  const sessionSetup = useSessionSetup(initialSetup, systemOptions, disciplineOptions);
  
  // Cache for question details to prevent redundant API calls
  const [questionDetailsCache, setQuestionDetailsCache] = useState(new Map());
  const [lastFetchedQuestionId, setLastFetchedQuestionId] = useState(null);
  
  const questionsData = useQuestionsData();
  const timeTracker = useTimeTracker();
    // Load exam types on component mount
  useEffect(() => {
    const loadExamTypes = async () => {
      try {
        // console.log('[PracticeTestContainer] Loading exam types...');
        const types = await services.examType.getExamTypes();
        // console.log('[PracticeTestContainer] Loaded exam types:', types?.length || 0, 'types');
        setExamTypes(types);
      } catch (error) {
        console.error('[PracticeTestContainer] Error loading exam types:', error);
      } finally {
        setIsLoadingExamTypes(false);
      }
    };
    
    loadExamTypes();
  }, []);
  
  // Load user folders on component mount
  useEffect(() => {
    const loadUserFolders = async () => {
      try {
        // console.log('[PracticeTestContainer] Loading user folders...');
        const folders = await services.userFolder.getAllFolders();
        // console.log('[PracticeTestContainer] Loaded user folders:', folders?.length || 0, 'folders');
        setUserFolders(folders || []);
      } catch (error) {
        console.error('[PracticeTestContainer] Error loading user folders:', error);
        setUserFolders([]);
      } finally {
        setIsLoadingFolders(false);
      }
    };
    
    loadUserFolders();
  }, []);
  
  // Handle exam type preselection from URL parameters
  useEffect(() => {
    if (examTypeParam && examTypes.length > 0) {
      // console.log('[PracticeTestContainer] Looking for exam type:', examTypeParam, 'in:', examTypes);
      const matchingExamType = examTypes.find(type => 
        type.name.toLowerCase() === examTypeParam.toLowerCase()
      );
      
      if (matchingExamType) {
        // console.log('[PracticeTestContainer] Preselecting exam type:', matchingExamType);
        sessionSetup.setSessionSetup(prev => ({
          ...prev,
          examType: matchingExamType.name,
          examTypeId: matchingExamType.id
        }));
      } else {
        console.warn('[PracticeTestContainer] No matching exam type found for:', examTypeParam);
      }
    }
  }, [examTypeParam, examTypes, sessionSetup.setSessionSetup]);
    // URL loading state management
  const [isLoadingExamFromUrl, setIsLoadingExamFromUrl] = useState(false);
  const [loadedExamUrlId, setLoadedExamUrlId] = useState(null);
  
  // Exam data state for URL navigation
  const [examData, setExamData] = useState(null);
  
  // Local state
  const [questionDetails, setQuestionDetails] = useState(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [isSidebarVisible, setIsSidebarVisible] = useState(() => {
    // Load sidebar visibility preference from localStorage, default to false (closed)
    const saved = localStorage.getItem('examflip-sidebar-visible');
    return saved !== null ? JSON.parse(saved) : false;
  });
  const [sidebarPage, setSidebarPage] = useState(0);  const [totalSidebarPages, setTotalSidebarPages] = useState(1);
  // eslint-disable-next-line no-unused-vars
  const [mockQuestions, setMockQuestions] = useState([]);
  
  // Saved lists and related state
  const [savedLists, setSavedLists] = useState(() => {
    const saved = localStorage.getItem('questionLists');
    return saved ? JSON.parse(saved) : {
      'Review Later': []
    };
  });
  const [selectedFolder, setSelectedFolder] = useState('Review Later');
  const [loadedList, setLoadedList] = useState(null);
  const [loadedListProgress, setLoadedListProgress] = useState(0);
  
  // AI-related state
  const [showAskAIModal, setShowAskAIModal] = useState(false);
  const [aiQuestion, setAIQuestion] = useState('');
  const [aiResponse, setAIResponse] = useState('');
  const [isAILoading, setIsAILoading] = useState(false);
  
  // Display state
  const [showAnswerOptions, setShowAnswerOptions] = useState(true);
  
  // Constants
  const QUESTIONS_PER_PAGE = 20;

  // 🚀 PERFORMANCE FIX: Memoize state setters to prevent unnecessary re-renders
  const memoizedSetExamData = useCallback((data) => setExamData(data), []);
  const memoizedSetSidebarPage = useCallback((page) => setSidebarPage(page), []);
  const memoizedSetSelectedFolder = useCallback((folder) => setSelectedFolder(folder), []);
  const memoizedSetShowAskAIModal = useCallback((show) => setShowAskAIModal(show), []);
  const memoizedSetAIQuestion = useCallback((question) => setAIQuestion(question), []);

    // Initialize question statuses
  useEffect(() => {
    if (questionsData.totalQuestions > 0) {
      questionState.setQuestionStatuses(Array(questionsData.totalQuestions).fill(null));
        // Calculate total sidebar pages
      setTotalSidebarPages(Math.ceil(questionsData.totalQuestions / QUESTIONS_PER_PAGE));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionsData.totalQuestions, questionState.setQuestionStatuses]);// Update question progress when question index changes
  useEffect(() => {
    if (sessionSetup.setupComplete) {
      services.navigation.updateQuestionProgress({
        currentQuestionIndex: questionState.currentQuestionIndex,
        totalQuestions: questionsData.totalQuestions,
        setCurrentQuestionNumber: questionsData.setCurrentQuestionNumber,
        setProgressPercentage: questionsData.setProgressPercentage
      });
      
      // Update sidebar page when question index changes to ensure visibility
      memoizedSetSidebarPage(Math.floor(questionState.currentQuestionIndex / QUESTIONS_PER_PAGE));
    }
  }, [questionState.currentQuestionIndex, questionsData.totalQuestions, questionsData.setCurrentQuestionNumber, questionsData.setProgressPercentage, sessionSetup.setupComplete]);
  
  // Save sidebar visibility preference to localStorage
  useEffect(() => {
    localStorage.setItem('examflip-sidebar-visible', JSON.stringify(isSidebarVisible));
  }, [isSidebarVisible]);
  
  // Fetch question details when the current question changes
  useEffect(() => {
    if (sessionSetup.setupComplete) {
      const fetchQuestionDetails = async () => {
        // console.log('🚨 [PracticeTestContainer] ═══ QUESTION DETAILS FETCH TRIGGERED ═══');
        // console.log('🚨 [PracticeTestContainer] 🔍 Fetching details for questionState.currentQuestionIndex:', questionState.currentQuestionIndex);
        // console.log('🚨 [PracticeTestContainer] 📊 Total questions available:', questionsData.questions?.length || 0);
        // console.log('🚨 [PracticeTestContainer] 🎯 setupComplete:', sessionSetup.setupComplete);
        
        const currentQ = services.question.getCurrentQuestion(
          questionsData.questions, 
          questionState.currentQuestionIndex, 
          mockQuestions
        );
        
        const questionIdentifier = currentQ?.questionId || currentQ?.id;
        
        // console.log('🚨 [PracticeTestContainer] 🔍 Question identifier resolved to:', questionIdentifier);
        
        if (!questionIdentifier) {
          // console.warn('🚨 [PracticeTestContainer] ⚠️ No valid question identifier found');
          setShowAnswerOptions(true);
          return;
        }
        
        // Check cache first
        if (questionDetailsCache.has(questionIdentifier)) {
          // console.log(`🚨 [PracticeTestContainer] ⚡ Cache hit for question ${questionIdentifier} - using cached data`);
          setQuestionDetails(questionDetailsCache.get(questionIdentifier));
          setIsLoadingDetails(false);
          setShowAnswerOptions(true);
          return;
        }

        // Prevent duplicate calls to the same question
        if (lastFetchedQuestionId === questionIdentifier) {
          // console.log(`🚨 [PracticeTestContainer] 🚫 Skipping duplicate call for question ${questionIdentifier}`);
          return;
        }
        
        // Record that we're fetching this question
        setLastFetchedQuestionId(questionIdentifier);
        setIsLoadingDetails(true);
        setShowAnswerOptions(false);
        
        // console.log(`🚨 [PracticeTestContainer] 🌐 ═══ API CALL STARTING ═══`);
        // console.log(`🚨 [PracticeTestContainer] 🌐 Making API call to get details for question ID: ${questionIdentifier}`);
        // console.log(`🚨 [PracticeTestContainer] 🌐 Expected URL should be: /api/question/${questionIdentifier}`);
        // console.log(`🚨 [PracticeTestContainer] 🌐 Call timestamp: ${new Date().toISOString()}`);
        
        try {
          const details = await services.question.getQuestionById(questionIdentifier);
          // console.log(`🚨 [PracticeTestContainer] ✅ ═══ API CALL SUCCESS ═══`);
          // console.log(`🚨 [PracticeTestContainer] ✅ Successfully fetched details for question ID: ${questionIdentifier}`);
          // console.log(`🚨 [PracticeTestContainer] 📄 Details response keys:`, Object.keys(details || {}));
          
          // Cache the result
          const newCache = new Map(questionDetailsCache);
          newCache.set(questionIdentifier, details);
          setQuestionDetailsCache(newCache);
          // console.log(`🚨 [PracticeTestContainer] 💾 Cached details for question ${questionIdentifier}. Cache size: ${newCache.size}`);
          
          setQuestionDetails(details);
        } catch (error) {
          console.error(`🚨 [PracticeTestContainer] ❌ ═══ API CALL FAILED ═══`);
          console.error(`🚨 [PracticeTestContainer] ❌ Error fetching details for question ID: ${questionIdentifier}`, error);
        } finally {
          // console.log(`🚨 [PracticeTestContainer] 🏁 API call completed, setting loading states...`);
          setIsLoadingDetails(false);
          setShowAnswerOptions(true);
        }
      };
      
      // console.log(`🚨 [PracticeTestContainer] 🔄 ═══ USEEFFECT DEPENDENCIES CHANGED ═══`);
      // console.log(`🚨 [PracticeTestContainer] 🔄 Dependencies that triggered this effect:`);
      // console.log(`🚨 [PracticeTestContainer] 🔄 - questionState.currentQuestionIndex: ${questionState.currentQuestionIndex}`);
      // console.log(`🚨 [PracticeTestContainer] 🔄 - questionsData.questions.length: ${questionsData.questions?.length || 0}`);
      // console.log(`🚨 [PracticeTestContainer] 🔄 - sessionSetup.setupComplete: ${sessionSetup.setupComplete}`);
      // console.log(`🚨 [PracticeTestContainer] 🔄 Effect trigger timestamp: ${new Date().toISOString()}`);
      
      fetchQuestionDetails();
    }
  }, [questionState.currentQuestionIndex, questionsData.questions?.length, sessionSetup.setupComplete]);

  // Fetch category data when exam type changes
  useEffect(() => {
    const fetchCategoryData = async () => {
      if (!sessionSetup.sessionSetup.examTypeId) {
        setSystemOptions([]);
        setDisciplineOptions([]);
        return;
      }

      try {
        // console.log('[PracticeTestContainer] Fetching category data for examTypeId:', sessionSetup.sessionSetup.examTypeId);
        
        // Fetch category groups for the selected exam type
        const categoryGroups = await services.categoryGroup.getCategoryGroupsByExamTypeId(sessionSetup.sessionSetup.examTypeId);
        
        // Find system and discipline groups
        const systemGroup = categoryGroups.find(g => g.name.toLowerCase() === 'system');
        const disciplineGroup = categoryGroups.find(g => g.name.toLowerCase() === 'discipline');

        // Fetch system categories
        if (systemGroup) {
          const systemCategories = await services.category.getCategoriesByGroupId(systemGroup.id);
          setSystemOptions(systemCategories);
        } else {
          setSystemOptions([]);
        }

        // Fetch discipline categories
        if (disciplineGroup) {
          const disciplineCategories = await services.category.getCategoriesByGroupId(disciplineGroup.id);
          setDisciplineOptions(disciplineCategories);
        } else {
          setDisciplineOptions([]);
        }
      } catch (error) {
        console.error('[PracticeTestContainer] Error fetching category data:', error);
        setSystemOptions([]);
        setDisciplineOptions([]);
      }
    };    fetchCategoryData();
  }, [sessionSetup.sessionSetup.examTypeId]);
  // Handle category preselection from URL parameters
  useEffect(() => {
    if (categoryParam && sessionSetup.sessionSetup.examTypeId && (systemOptions.length > 0 || disciplineOptions.length > 0)) {
      // Find the category in either system or discipline options
      const categoryInSystem = systemOptions.find(cat => 
        cat.name.toLowerCase() === categoryParam.toLowerCase()
      );
      const categoryInDiscipline = disciplineOptions.find(cat => 
        cat.name.toLowerCase() === categoryParam.toLowerCase()
      );
        if (categoryInSystem) {
        // console.log('[PracticeTestContainer] Preselecting system category:', categoryInSystem);
        // Preselect the system category
        sessionSetup.setSessionSetup(prev => ({
          ...prev,
          system: [categoryInSystem.name],
          discipline: ["ALL"]
        }));
      } else if (categoryInDiscipline) {
        // console.log('[PracticeTestContainer] Preselecting discipline category:', categoryInDiscipline);
        // Preselect the discipline category
        sessionSetup.setSessionSetup(prev => ({
          ...prev,
          system: ["ALL"],
          discipline: [categoryInDiscipline.name]
        }));
      }
    }
  }, [categoryParam, sessionSetup.sessionSetup.examTypeId, systemOptions, disciplineOptions, sessionSetup.setSessionSetup]);
  
  // Enhanced URL loading: Load exam session from URL parameter (both resume and direct access)
  useEffect(() => {
    if (examIdParam) {
      // Check if we are already loading this ID or have already loaded it
      if (isLoadingExamFromUrl || loadedExamUrlId === examIdParam) {
        return; // Do nothing if already loading or if this ID has been processed
      }

      const fetchExamDataForUrl = async () => {
        setIsLoadingExamFromUrl(true);
        
        const isResuming = resumeParam === 'true';
        // console.log(isResuming ? 'Resuming exam session with ID:' : 'Loading exam from URL with ID:', examIdParam);
        
        try {
          // Reset relevant states before fetching new exam data
          questionsData.setLoading(true);
          questionsData.setError(null);
          
          // Load the existing exam session
          const sessionData = await services.exam.getExamSessionById(parseInt(examIdParam));
          
          if (!sessionData) {
            throw new Error(`Failed to load exam session with ID: ${examIdParam}. The exam may not exist or may have been deleted.`);
          }
            // console.log('Loaded exam session data:', sessionData);
          
          // CRITICAL FIX: Set examData so PracticeTest can access timeSpent for timer resume
          memoizedSetExamData(sessionData);
          // console.log('[PracticeTestContainer] Set examData for timer resume:', { 
          //   id: sessionData.id, 
          //   timeSpent: sessionData.timeSpent 
          // });
          
          // Set up the session state
          sessionSetup.setCurrentSessionId(sessionData.id);
          sessionSetup.setSetupComplete(true);
          
          // Initialize time tracker for the session
          // For resumed sessions, preserve the time spent; for direct access, start fresh
          timeTracker.setTimeTracker({
            questionStartTime: Date.now(),
            totalTimeSpent: isResuming ? (sessionData.timeSpent || 0) : 0
          });
          
          // Fetch questions for the session
          const sessionDataResult = await questionsData.fetchQuestionsForSession(sessionData.id, mockQuestions);
          
          // Save exam data and questions to session storage for resume functionality
          if (sessionDataResult) {
            examSessionStorage.saveExamSession(sessionData, sessionData.id);
            if (sessionDataResult.questions) {
              examSessionStorage.saveQuestions(sessionDataResult.questions);
            }
            if (sessionDataResult.responses) {
              examSessionStorage.saveResponses(sessionDataResult.responses);
            }
          }
          
          // console.log(isResuming ? 'Successfully resumed exam session' : 'Successfully loaded exam from URL');
          
          // Mark this ID as processed
          setLoadedExamUrlId(examIdParam);
          
        } catch (error) {
          console.error('Error loading exam session:', error);
          const errorMessage = error.message.includes('Failed to load exam session') 
            ? error.message 
            : 'Failed to load the exam session. Please check the URL and try again.';
          questionsData.setError(errorMessage);
          
          // Mark as processed even on error to avoid retrying this ID in a loop
          setLoadedExamUrlId(examIdParam);
        } finally {
          questionsData.setLoading(false);
          setIsLoadingExamFromUrl(false);
        }
      };

      fetchExamDataForUrl();
      
    } else {
      // No examIdParam: This is the path for a new exam or resume from storage.
      // Reset states related to URL loading if necessary.
      setIsLoadingExamFromUrl(false);
      setLoadedExamUrlId(null);
    }
    
    // CRITICAL: Only examIdParam and resumeParam as dependencies
  }, [examIdParam, resumeParam]);
  // Handler to start a new session
  const handleStartSession = useCallback(async () => {
    try {
      // Reset URL loading states when starting new session
      setIsLoadingExamFromUrl(false);
      setLoadedExamUrlId(null);
        // console.log('[PracticeTestContainer] Starting session...');
      const sessionResponse = await services.session.startSession({
        sessionSetup: sessionSetup.sessionSetup,
        systemCategories: systemOptions,
        disciplineCategories: disciplineOptions,
        setLoading: questionsData.setLoading,
        setError: questionsData.setError,
        setCurrentSessionId: sessionSetup.setCurrentSessionId,
        setSetupComplete: sessionSetup.setSetupComplete,
        setTimeTracker: timeTracker.setTimeTracker
      }, services);
        // console.log('[PracticeTestContainer] Session started, response:', sessionResponse);
      
      // Update examData state to trigger URL navigation
      if (sessionResponse) {
        memoizedSetExamData({
          id: sessionResponse.id,
          ...sessionResponse
        });
        // console.log('[PracticeTestContainer] Updated examData for URL navigation:', { id: sessionResponse.id });
      }
      
      // 🎯 FIX: Use the session ID from the response, not the stale state
      const sessionId = sessionResponse?.id || sessionSetup.currentSessionId;
      if (sessionId) {
        // console.log('[PracticeTestContainer] Fetching questions for session:', sessionId);
        const sessionDataResult = await questionsData.fetchQuestionsForSession(sessionId, mockQuestions);
        
        // Save exam data and questions to session storage for resume functionality
        if (sessionDataResult && sessionResponse) {
          examSessionStorage.saveExamSession(sessionResponse, sessionResponse.id);
          if (sessionDataResult.questions) {
            examSessionStorage.saveQuestions(sessionDataResult.questions);
          }
          if (sessionDataResult.responses) {
            examSessionStorage.saveResponses(sessionDataResult.responses);
          }
        }
      } else {
        console.error('[PracticeTestContainer] No session ID available to fetch questions');
        questionsData.setError('Failed to get session ID for question fetching');
      }
    } catch (error) {
      console.error('Error starting session:', error);
    }
  }, [sessionSetup.sessionSetup, systemOptions, disciplineOptions, questionsData, sessionSetup.setCurrentSessionId, sessionSetup.setSetupComplete, sessionSetup.currentSessionId, timeTracker.setTimeTracker, mockQuestions, memoizedSetExamData]);
  
  // Handler to end a session
  const handleEndSession = async () => {
    try {
      await services.session.endSession({
        sessionId: sessionSetup.currentSessionId,
        timeTracker: timeTracker.timeTracker,
        sessionTitle: sessionSetup.sessionSetup.title,
        setIsSessionEnding: sessionSetup.setIsSessionEnding,
        resetSessionState: () => {
          sessionSetup.resetSessionState();
          questionState.resetQuestionState();
          memoizedSetSidebarPage(0);
          setLoadedList(null);
          setLoadedListProgress(0);
        }
      }, services);
    } catch (error) {
      console.error('Error ending session:', error);
    }
  };
  
  // Handler to check an answer
  const handleCheckAnswer = async () => {
    if (!questionState.selectedAnswer) return;
    
    try {
      const isCorrect = await services.answerService.submitAnswer({
        currentSessionId: sessionSetup.currentSessionId,
        currentQuestion: services.question.getCurrentQuestion(
          questionsData.questions, 
          questionState.currentQuestionIndex, 
          mockQuestions
        ),
        selectedAnswer: questionState.selectedAnswer,
        timeTracker: timeTracker.timeTracker,
        isFlagged: questionState.isFlagged,
        setIsCorrect: questionState.setIsCorrect,
        setQuestionStatuses: questionState.setQuestionStatuses,
        currentQuestionIndex: questionState.currentQuestionIndex,
        setTimeTracker: timeTracker.setTimeTracker
      }, services);
      
      questionState.setIsAnswerChecked(true);
      return isCorrect;
    } catch (error) {
      console.error('Error checking answer:', error);
      return false;
    }
  };
  
  // Navigation handlers
  const handlePreviousQuestion = useCallback(() => {
    services.navigation.navigateToPreviousQuestion({
      currentQuestionIndex: questionState.currentQuestionIndex,
      setCurrentQuestionIndex: questionState.setCurrentQuestionIndex,
      questionHistory: questionState.questionHistory,
      resetQuestionState: questionState.resetQuestionState
    });
  }, [questionState.currentQuestionIndex, questionState.setCurrentQuestionIndex, questionState.questionHistory, questionState.resetQuestionState]);
  
  const handleNextQuestion = useCallback(() => {
    services.navigation.navigateToNextQuestion({
      currentQuestionIndex: questionState.currentQuestionIndex,
      setCurrentQuestionIndex: questionState.setCurrentQuestionIndex,
      questions: questionsData.questions,
      mockQuestions,
      addToQuestionHistory: questionState.addToQuestionHistory,
      resetQuestionState: questionState.resetQuestionState
    });
  }, [questionState.currentQuestionIndex, questionState.setCurrentQuestionIndex, questionsData.questions, mockQuestions, questionState.addToQuestionHistory, questionState.resetQuestionState]);
  
  const handleGoToQuestion = useCallback((index) => {
    services.navigation.navigateToQuestionByIndex({
      targetIndex: index,
      currentQuestionIndex: questionState.currentQuestionIndex,
      setCurrentQuestionIndex: questionState.setCurrentQuestionIndex,
      addToQuestionHistory: questionState.addToQuestionHistory,
      resetQuestionState: questionState.resetQuestionState
    });
  }, [questionState.currentQuestionIndex, questionState.setCurrentQuestionIndex, questionState.addToQuestionHistory, questionState.resetQuestionState]);
  
  const handleNextQuestionInList = () => {
    return services.navigation.navigateToNextQuestionInList({
      currentQuestionIndex: questionState.currentQuestionIndex,
      setCurrentQuestionIndex: questionState.setCurrentQuestionIndex,
      loadedList,
      addToQuestionHistory: questionState.addToQuestionHistory,
      resetQuestionState: questionState.resetQuestionState,
      setLoadedListProgress
    });
  };
  
  // List management handlers
  const handleSaveQuestion = (listName) => {
    const currentIdx = questionState.currentQuestionIndex;
    const updatedLists = { ...savedLists };
    
    if (!updatedLists[listName]) {
      updatedLists[listName] = [];
    }
    
    if (!updatedLists[listName].includes(currentIdx)) {
      updatedLists[listName].push(currentIdx);
      setSavedLists(updatedLists);
      localStorage.setItem('questionLists', JSON.stringify(updatedLists));
    }
  };
  
  const handleLoadList = (listName) => {
    if (savedLists[listName] && savedLists[listName].length > 0) {
      setLoadedList({
        name: listName,
        questions: savedLists[listName]
      });
      setLoadedListProgress(1); // Start at the first question
      
      // Navigate to the first question in the list
      handleGoToQuestion(savedLists[listName][0]);
    }
  };
  
  const handleDeleteList = (listName) => {
    if (listName === 'Review Later') return; // Don't delete the default list
    
    const updatedLists = { ...savedLists };
    delete updatedLists[listName];
    
    setSavedLists(updatedLists);
    localStorage.setItem('questionLists', JSON.stringify(updatedLists));
    
    if (loadedList && loadedList.name === listName) {
      setLoadedList(null);
      setLoadedListProgress(0);
    }
  };
  
  // AI handlers
  const handleAskAI = async () => {
    if (!aiQuestion.trim()) return;
    
    try {
      setIsAILoading(true);
      const currentQ = services.question.getCurrentQuestion(
        questionsData.questions, 
        questionState.currentQuestionIndex, 
        mockQuestions
      );
      
      const aiData = {
        questionId: currentQ.id || currentQ.questionId || questionState.currentQuestionIndex, 
        query: aiQuestion.trim(),
        sessionId: sessionSetup.currentSessionId
      };
      
      const response = await services.ai.askAI(aiData);
      setAIResponse(response.data || response);
    } catch (err) {
      console.error('Error asking AI:', err);
      setAIResponse('Sorry, there was an error processing your question. Please try again later.');
    } finally {
      setIsAILoading(false);
    }
  };
  
  // Helper functions
  const getSelectedSystemsText = useCallback(() => {
    return sessionSetup.sessionSetup.system.includes('ALL') 
      ? 'All Systems' 
      : sessionSetup.sessionSetup.system.join(', ');
  }, [sessionSetup.sessionSetup.system]);
  
  const getSelectedDisciplinesText = useCallback(() => {
    return sessionSetup.sessionSetup.discipline.includes('ALL') 
      ? 'All Disciplines' 
      : sessionSetup.sessionSetup.discipline.join(', ');
  }, [sessionSetup.sessionSetup.discipline]);

  // Memoized sidebar toggle function
  const toggleSidebar = useCallback(() => {
    setIsSidebarVisible(!isSidebarVisible);
  }, [isSidebarVisible]);
  
  // DEBUG: Add logging for questionsData changes
  useEffect(() => {
    // console.log('[PracticeTestContainer] questionsData updated:', {
    //   questionsLength: questionsData.questions?.length || 0,
    //   totalQuestions: questionsData.totalQuestions,
    //   loading: questionsData.loading,
    //   error: questionsData.error,
    //   timestamp: new Date().toISOString()
    // });npm start

  }, [questionsData.questions, questionsData.totalQuestions, questionsData.loading, questionsData.error]);

  // console.log('[PracticeTestContainer] Rendering PracticeTest with examTypes:', examTypes?.length || 0, 'types, isLoadingExamTypes:', isLoadingExamTypes, ', userFolders:', userFolders?.length || 0, 'folders, isLoadingFolders:', isLoadingFolders);

  return (
    <div>
      <PracticeTest
      // Theme
      isDarkMode={isDarkMode}
      themeStyles={themeStyles}
        // Session setup
      sessionSetup={sessionSetup.sessionSetup}
      setSessionSetup={sessionSetup.setSessionSetup}
      setupComplete={sessionSetup.setupComplete}
      availableQuestionsCount={sessionSetup.availableQuestionsCount}      isLoadingQuestionCount={sessionSetup.isLoadingQuestionCount}
      fetchQuestionCount={sessionSetup.fetchQuestionCount}
      currentSessionId={sessionSetup.currentSessionId}
      isSessionEnding={sessionSetup.isSessionEnding}
      titleError={sessionSetup.titleError}
      questionCountError={sessionSetup.questionCountError}
      
      // Exam types - pass down to prevent duplicate API calls
      examTypes={examTypes}
      isLoadingExamTypes={isLoadingExamTypes}
      
      // User folders - pass down to prevent duplicate API calls
      userFolders={userFolders}
      isLoadingFolders={isLoadingFolders}
      
      // Category options
      systemOptions={systemOptions}
      disciplineOptions={disciplineOptions}
      
      // Exam data for URL navigation
      examData={examData}
      setExamData={memoizedSetExamData}
      
      // Question state
      currentQuestionIndex={questionState.currentQuestionIndex}
      selectedAnswer={questionState.selectedAnswer}
      setSelectedAnswer={questionState.setSelectedAnswer}
      isAnswerChecked={questionState.isAnswerChecked}
      isCorrect={questionState.isCorrect}
      isFlagged={questionState.isFlagged}
      showClues={questionState.showClues}
      questionStatuses={questionState.questionStatuses}
      questionHistory={questionState.questionHistory}

      // Questions data
      questions={questionsData.questions}
      responses={questionsData.responses}
      loading={questionsData.loading || isLoadingExamFromUrl}
      error={questionsData.error}
      totalQuestions={questionsData.totalQuestions}
      currentQuestionNumber={questionsData.currentQuestionNumber}
      progressPercentage={questionsData.progressPercentage}
      
      // Question details
      questionDetails={questionDetails}
      isLoadingDetails={isLoadingDetails}
      showAnswerOptions={showAnswerOptions}
      
      // Sidebar
      isSidebarVisible={isSidebarVisible}
      toggleSidebar={toggleSidebar}
      sidebarPage={sidebarPage}
      setSidebarPage={memoizedSetSidebarPage}
      totalSidebarPages={totalSidebarPages}
      
      // Saved lists
      savedLists={savedLists}
      selectedFolder={selectedFolder}
      setSelectedFolder={memoizedSetSelectedFolder}
      loadedList={loadedList}
      loadedListProgress={loadedListProgress}
      
      // AI
      showAskAIModal={showAskAIModal}
      setShowAskAIModal={memoizedSetShowAskAIModal}
      aiQuestion={aiQuestion}
      setAIQuestion={memoizedSetAIQuestion}
      aiResponse={aiResponse}
      isAILoading={isAILoading}
      
      // Helper functions
      getSelectedSystemsText={getSelectedSystemsText}
      getSelectedDisciplinesText={getSelectedDisciplinesText}
      
      // Action handlers
      handleStartSession={handleStartSession}
      handleEndSession={handleEndSession}
      handleCheckAnswer={handleCheckAnswer}
      handlePreviousQuestion={handlePreviousQuestion}
      handleNextQuestion={handleNextQuestion}
      handleGoToQuestion={handleGoToQuestion}
      handleNextQuestionInList={handleNextQuestionInList}
      handleSaveQuestion={handleSaveQuestion}
      handleLoadList={handleLoadList}
      handleDeleteList={handleDeleteList}
      handleAskAI={handleAskAI}
      
      // Time tracker
      timeTracker={timeTracker.timeTracker}
      setTimeTracker={timeTracker.setTimeTracker}
      
      // Mock questions (for testing)
      mockQuestions={mockQuestions}
      />
    </div>
  );
};

export default PracticeTestContainer;
